import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiClient } from '../../api/client';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Store, CalendarDays, QrCode, Users } from 'lucide-react';

interface LayoutContext {
  viewAsStoreId?: string;
  viewAsStoreName?: string;
}

interface Stats {
  stores: { total: number; active: number };
  campaigns: { total: number };
  runs: { total: number; active: number };
  scans: { total: number };
  customers: { total: number };
}

interface MonthlyData {
  chartData: Array<Record<string, string | number>>;
  campaigns: string[];
}

interface ActiveRunData {
  runId: string;
  campaignName: string;
  instanceNumber: number;
  storeData: Array<{ name: string; count: number }>;
}

const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

export default function DashboardPage() {
  const { viewAsStoreId, viewAsStoreName } = useOutletContext<LayoutContext>() || {};
  const [stats, setStats] = useState<Stats | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyData | null>(null);
  const [activeRuns, setActiveRuns] = useState<ActiveRunData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const isStoreView = !!viewAsStoreId;

  useEffect(() => {
    loadData();
  }, [selectedYear, viewAsStoreId]);

  const loadData = async () => {
    setLoading(true);
    // Reset data when switching views to avoid showing stale data
    setStats(null);
    setMonthlyData(null);
    setActiveRuns([]);

    try {
      const storeParam = viewAsStoreId ? `&storeId=${viewAsStoreId}` : '';
      const storeParamFirst = viewAsStoreId ? `?storeId=${viewAsStoreId}` : '';

      const [statsRes, monthlyRes, activeRunsRes] = await Promise.all([
        apiClient.get<{ success: boolean; data: Stats }>(`/admin/dashboard${storeParamFirst}`),
        apiClient.get<{ success: boolean; data: MonthlyData }>(`/admin/dashboard/scans-per-month?year=${selectedYear}${storeParam}`),
        apiClient.get<{ success: boolean; data: ActiveRunData[] }>(`/admin/dashboard/active-runs-store-scans${storeParamFirst}`),
      ]);

      setStats(statsRes.data.data);
      setMonthlyData(monthlyRes.data.data);
      setActiveRuns(activeRunsRes.data.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  const years = [2024, 2025, 2026, 2027];

  // Build links with viewAs param preserved
  const buildLink = (path: string) => {
    if (viewAsStoreId) {
      return `${path}?viewAs=${viewAsStoreId}`;
    }
    return path;
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">
        {isStoreView ? 'My Dashboard' : 'Dashboard'}
      </h1>

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isStoreView ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6 mb-8`}>
        {!isStoreView && (
          <StatCard
            title="Participating Stores"
            value={stats?.stores.total || 0}
            subtitle={`${stats?.stores.active || 0} active`}
            icon={Store}
            bgColor="bg-indigo-500"
            href="/admin/stores"
          />
        )}
        <StatCard
          title={isStoreView ? "My Campaigns" : "Campaign Runs"}
          value={stats?.runs.total || 0}
          subtitle={`${stats?.runs.active || 0} active`}
          icon={CalendarDays}
          bgColor="bg-sky-500"
          href={buildLink("/admin/campaigns")}
        />
        <StatCard
          title={isStoreView ? "My Scans" : "Total Scans"}
          value={stats?.scans.total || 0}
          subtitle="All time"
          icon={QrCode}
          bgColor="bg-violet-500"
          href={buildLink(isStoreView ? "/admin/qrcodes" : "/admin/campaigns")}
        />
        <StatCard
          title={isStoreView ? "My Customers" : "Customers"}
          value={stats?.customers.total || 0}
          subtitle="Unique entries"
          icon={Users}
          bgColor="bg-blue-500"
          href={buildLink("/admin/customers")}
        />
      </div>

      {/* Bar Chart - Scans per Month */}
      <div className="card mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">{isStoreView ? 'My Scans per Month' : 'Scans by Campaign per Month'}</h2>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="input w-32"
          >
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {monthlyData && monthlyData.campaigns.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData.chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280' }} />
              <YAxis tick={{ fill: '#6b7280' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              />
              <Legend />
              {monthlyData.campaigns.map((campaign, index) => (
                <Bar
                  key={campaign}
                  dataKey={campaign}
                  fill={COLORS[index % COLORS.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center text-gray-500 py-12">
            No scan data for {selectedYear}
          </div>
        )}
      </div>

      {/* Pie Charts - Active Campaign Runs */}
      {activeRuns.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Active Campaigns - Scans by Store</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {activeRuns.map((run) => (
              <div key={run.runId} className="card">
                <h3 className="font-medium text-gray-900 mb-4">
                  {run.campaignName} (Run #{run.instanceNumber})
                </h3>
                {run.storeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={run.storeData}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {run.storeData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No scans yet
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="space-y-2">
            {isStoreView ? (
              <>
                <a href={buildLink("/admin/qrcodes")} className="btn-primary block text-center">
                  View My QR Codes
                </a>
                <a href={buildLink("/admin/campaigns")} className="btn-secondary block text-center">
                  View My Campaigns
                </a>
                <a href={buildLink("/admin/store-settings")} className="btn-secondary block text-center">
                  Store Settings
                </a>
              </>
            ) : (
              <>
                <a href="/admin/campaigns" className="btn-primary block text-center">
                  Manage Campaigns
                </a>
                <a href="/admin/stores" className="btn-secondary block text-center">
                  Manage Stores
                </a>
                <a href="/admin/customers" className="btn-secondary block text-center">
                  View Customers
                </a>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4">{isStoreView ? 'Tips' : 'Campaign Templates'}</h2>
          {isStoreView ? (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Get the most out of your participation:
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Download and print your QR codes</li>
                <li>• Place them near checkout or entrances</li>
                <li>• Encourage customers to scan for prizes</li>
                <li>• Track your scan progress on this dashboard</li>
              </ul>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Tip: Name your campaigns with seasons or months for easy identification:
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Discovery Event - Spring 2026</li>
                <li>• Discovery Event - Summer 2026</li>
                <li>• Holiday Shopping - December</li>
                <li>• Back to School - August</li>
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  bgColor,
  href,
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  bgColor: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className={`${bgColor} rounded-xl p-6 text-white hover:opacity-90 transition-opacity cursor-pointer block`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-white/80 mb-1">{title}</p>
          <p className="text-3xl font-bold">{value.toLocaleString()}</p>
          <p className="text-sm text-white/70 mt-1">{subtitle}</p>
        </div>
        <Icon className="w-8 h-8 text-white/80" />
      </div>
    </a>
  );
}
